import { forwardRef, type ReactNode } from "react";
import { ChevronDown, X } from "lucide-react";
import { cn } from "src/shadcn/lib/utils";
import { SIZE_CONFIG } from "./constants";

interface TreeSelectTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  size: "small" | "medium" | "large";
  currentValue: string;
  selectedLabel: string;
  placeholder?: string;
  isDisabled: boolean;
  error: string;
  showClearButton: boolean;
  handleClear: (e: React.MouseEvent) => void;
  suffixIcon?: ReactNode;
}

export const TreeSelectTrigger = forwardRef<
  HTMLButtonElement,
  TreeSelectTriggerProps
>(
  (
    {
      size,
      currentValue,
      selectedLabel,
      placeholder,
      isDisabled,
      error,
      showClearButton,
      handleClear,
      suffixIcon,
      className,
      ...rest
    },
    ref
  ) => (
    <button
      ref={ref}
      type="button"
      disabled={isDisabled}
      {...rest}
      className={cn(
        "inline-flex w-full items-center gap-1 rounded-md border border-input bg-background outline-none",
        "hover:bg-accent focus:ring-1 focus:ring-ring",
        "disabled:pointer-events-none disabled:opacity-50",
        error && "border-destructive",
        SIZE_CONFIG[size].trigger,
        SIZE_CONFIG[size].text,
        className
      )}
    >
      <span
        className={cn(
          "flex-1 truncate text-left",
          !currentValue && "text-muted-foreground"
        )}
      >
        {selectedLabel || placeholder || ""}
      </span>
      {showClearButton && (
        <span
          role="button"
          tabIndex={-1}
          aria-label="Clear selection"
          className="shrink-0 text-muted-foreground hover:text-foreground"
          onClick={handleClear}
        >
          <X className="size-3.5" />
        </span>
      )}
      {suffixIcon ?? <ChevronDown className="size-4 shrink-0 opacity-50" />}
    </button>
  )
);

TreeSelectTrigger.displayName = "TreeSelectTrigger";
